import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth, NAV_SECTION_PATHS } from "@/context/AuthContext";
import FeedbackWidget, { FeedbackNavButton } from "@/components/FeedbackWidget";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import SearchOverlay from "@/components/SearchOverlay";
import { TedDrawer } from "@/components/TedDrawer";
import { ExpenseForm } from "@/pages/ExpensesPage";
import { Receipt } from "lucide-react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  ClipboardList,
  FolderKanban,
  Users as UsersIcon,
  Package,
  CalendarRange,
  Truck,
  FileText,
  ShoppingCart,
  Database,
  Lightbulb,
  Sparkles,
  Store,
  DatabaseZap,
  FileSpreadsheet,
  CalendarDays,
  LifeBuoy,
  Megaphone,
  Clock,
  Search,
  ChevronDown,
  ChevronRight,
  LogOut,
  ShieldCheck,
  EyeOff,
  UserCog,
  Check,
  ExternalLink,
  Landmark,
  Boxes,
  MapPin,
  History,
  ScanBarcode,
  Moon,
  Sun,
  StickyNote,
  ListTodo,
  Building2,
  AlertTriangle,
  MessageSquare as MessageSquareIcon,
  MonitorSmartphone,
  Plug,
  TrendingUp,
  Mail,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-nav-dashboard", perm: "dashboard.view" },
  { to: "/tasks", label: "Jobs", icon: ClipboardList, testId: "sidebar-nav-tasks", anyOf: ["orders.view", "operations.view"] },
  { to: "/users", label: "Customers", icon: UsersIcon, testId: "sidebar-nav-users", perm: "customers.view" },
  { to: "/products", label: "Products", icon: Package, testId: "sidebar-nav-products", anyOf: ["products.view", "stock.view"] },
  { to: "/orders", label: "Orders", icon: Package, testId: "sidebar-nav-orders", perm: "orders.view", sub: true },
  { to: "/quotes", label: "Quotes", icon: FileText, testId: "sidebar-nav-quotes", perm: "quotes.view" },
  { to: "/forms", label: "Forms", icon: FileSpreadsheet, testId: "sidebar-nav-forms", perm: "forms.view" },
  { to: "/holidays", label: "Holidays", icon: CalendarDays, testId: "sidebar-nav-holidays", perm: "holidays.request" },
  { to: "/tickets", label: "Tickets", icon: LifeBuoy, testId: "sidebar-nav-tickets", perm: "tickets.create" },
  { to: "/timesheets", label: "Timesheets", icon: Clock, testId: "sidebar-nav-timesheets", perm: "timesheets.view" },
  { to: "/web-sales", label: "Web Sales", icon: ShoppingCart, testId: "sidebar-nav-web-sales", perm: "orders.view" },
  { to: "/scheduler", label: "Scheduler", icon: CalendarRange, testId: "sidebar-nav-scheduler", perm: "operations.view" },
  { to: "/plant-transport", label: "Plant Transport", icon: Truck, testId: "sidebar-nav-plant-transport", anyOf: ["deliveries.view", "operations.view"] },
  { to: "/finance/reports?report=pnl", label: "Finance", icon: Landmark, testId: "sidebar-nav-finance", perm: "finance.profit_loss.view" },
];

function navClass({ isActive }) {
  return [
    "flex items-center gap-3 px-3 py-2 rounded-xl text-[14px] font-medium transition-colors duration-150",
    isActive
      ? "bg-indigo-600/[0.08] text-indigo-700"
      : "text-slate-800 hover:bg-slate-200/60 hover:text-slate-900",
  ].join(" ");
}

const subLinkClass = ({ isActive }) => [
  "flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl text-[14px] font-medium transition-colors duration-150",
  isActive ? "bg-indigo-600/[0.08] text-indigo-700" : "text-slate-700 hover:bg-slate-200/60 hover:text-slate-900",
].join(" ");

function NavGroup({ icon: Icon, label, open, onToggle, testId, children }) {
  return (
    <>
      <button type="button" onClick={onToggle} data-testid={testId}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-150">
        <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
        <span className="flex-1 text-left">{label}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="ml-4 pl-3 border-l border-slate-100 space-y-0.5">{children}</div>}
    </>
  );
}

function SubLink({ to, label, testId, setMobileOpen, count, alert, end }) {
  const loc = useLocation();
  const q = to.includes("?") ? new URLSearchParams(to.split("?")[1]) : null;
  const cls = q ? ({ isActive }) => subLinkClass({ isActive: isActive && [...q].every(([k, v]) => new URLSearchParams(loc.search).get(k) === v) }) : subLinkClass;
  return (
    <NavLink to={to} end={end} className={cls} data-testid={testId} onClick={() => setMobileOpen(false)}>
      <span>{label}</span>
      {alert > 0 && <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-sky-500 text-white text-[10px] font-bold" title="New enquiries not opened yet" data-testid={`${testId}-new-badge`}>{alert}</span>}
      {count != null && <span className="text-[10px] text-slate-400 tabular-nums">{count}</span>}
    </NavLink>
  );
}

export default function AppShell() {
  const {
    user,
    logout,
    masking,
    toggleMasking,
    can,
    canAny,
    isSuper,
    roles,
    previewRoleKey,
    setPreviewRole,
    previewing,
    effectiveRole,
    effectiveRoleName,
    moduleOn,
    isPlatformAdmin,
    supportSession,
    navHidden,
  } = useAuth();
  const hide = (k) => navHidden.has(k);
  const [expOpen, setExpOpen] = useState(false);
  const location = useLocation();
  const blockedRoute = [...navHidden].some((k) => (NAV_SECTION_PATHS[k] || []).some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`)));
  const companies = user?.companies || [];
  useEffect(() => {
    const active = user?.active_company_id;
    const stored = localStorage.getItem("operix_company_id");
    if (active && (!stored || (companies.length && !companies.some((c) => c.company_id === stored)))) {
      localStorage.setItem("operix_company_id", active);
    }
  }, [user]);
  const switchCompany = (cid) => { localStorage.setItem("operix_company_id", cid); window.location.reload(); };
  const endSupportSession = async () => {
    try { if (supportSession?.session_id) await api.post(`/platform/support-session/${supportSession.session_id}/end`); } catch (e) { /* ignore */ }
    localStorage.removeItem("operix_support_session");
    localStorage.removeItem("operix_support_company");
    window.location.href = "/platform";
  };
  const switchableRoles = (roles || []).filter((r) => !r.hidden);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("htss_theme") === "dark");
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", dark);
    localStorage.setItem("htss_theme", dark ? "dark" : "light");
  }, [dark]);
  const [dbTables, setDbTables] = useState([]);
  const [crewCount, setCrewCount] = useState(null);
  const [vehicleCount, setVehicleCount] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  const toggleGroup = (k) => setOpenGroup((g) => (g === k ? null : k));
  const [dataGroup, setDataGroup] = useState(null);
  const [intelOn, setIntelOn] = useState(false);
  useEffect(() => {
    api.get("/intel/enabled").then((r) => setIntelOn(!!r.data.enabled)).catch(() => setIntelOn(false));
  }, []);
  const [ted, setTed] = useState(null);
  const [salesIntel, setSalesIntel] = useState(false);
  useEffect(() => {
    api.get("/sales-intel/status").then((r) => setSalesIntel(!!(r.data.enabled && r.data.can_confirm))).catch(() => setSalesIntel(false));
  }, []);
  const [tedOpen, setTedOpen] = useState(false);
  useEffect(() => {
    api.get("/ted/status").then((r) => setTed(r.data.enabled ? r.data : null)).catch(() => setTed(null));
  }, []);
  useEffect(() => {
    const h = () => { setTedOpen(true); };
    window.addEventListener("ted:open", h);
    return () => { window.removeEventListener("ted:open", h); };
  }, []);
  const [dbOpen, setDbOpen] = useState(false);
  const toggleData = (k) => setDataGroup((g) => (g === k ? null : k));
  useEffect(() => {
    const p = location.pathname;
    const map = [
      ["comms", ["/communications/settings", "/data/whatsapp", "/data/email-launch", "/admin/notifications"]],
      ["people", ["/admin/users-roles", "/settings/company"]],
      ["commerce", ["/stock/woocommerce", "/epos/settings", "/settings/shipping"]],
      ["datamgmt", ["/data/woocommerce-history", "/imports", "/admin/quarantine"]],
      ["system", ["/settings/integrations", "/admin/activity-log"]],
    ];
    const hit = map.find(([, ps]) => ps.some((x) => p === x || p.startsWith(`${x}/`)));
    if (hit) setDataGroup(hit[0]);
    if (["/databases", "/crews", "/plant-transport", "/admin/log-viewer"].some((x) => p === x || p.startsWith(`${x}/`))) setDbOpen(true);
  }, [location.pathname]);

  const [srOpen, setSrOpen] = useState(0);
  const [shopOpen, setShopOpen] = useState(0);
  const [newEnq, setNewEnq] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    if (!(can("stock.view") || can("operations.view"))) return undefined;
    let live = true;
    const load = () => api.get("/stock-requests", { params: { status: "open" } })
      .then((r) => { if (live) setSrOpen(r.data.open_total || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (!can("orders.view")) return undefined;
    let live = true;
    const load = () => api.get("/shop/open-count")
      .then((r) => { if (live) setShopOpen(r.data.open || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (!canAny(["orders.view", "operations.view"])) return undefined;
    let live = true;
    const load = () => api.get("/tasks-inbound/new").then((r) => { if (live) setNewEnq(r.data.count || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
    // eslint-disable-next-line
  }, []);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [anns, setAnns] = useState([]);
  const [returnsCount, setReturnsCount] = useState(0);

  const loadNotifs = useCallback(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    if (can("mobile.returns.inspect")) {
      api.get("/returns/pending-count").then((r) => setReturnsCount(r.data.count || 0)).catch(() => {});
    }
    if (can("announcements.view")) {
      api.get("/announcements").then((r) => setAnns((r.data.items || []).filter((a) => !a.read))).catch(() => {});
    }
  }, [can]);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  useEffect(() => {
    if (can("databases.view")) {
      api.get("/databases").then((r) => setDbTables(r.data.tables || [])).catch(() => {});
      api.get("/crews").then((r) => setCrewCount((r.data.crews || []).length)).catch(() => {});
    }
    if (can("deliveries.view") || can("operations.view")) {
      api.get("/vehicles").then((r) => setVehicleCount((r.data.items || []).length)).catch(() => {});
    }
  }, [can]);

  const dc = {
    comms: can("communications.providers"), whatsapp: can("whatsapp.view") || can("whatsapp.settings.manage"),
    emailLaunch: canAny(["email_marketing.view", "email_marketing.manage"]), notif: can("users.manage"),
    staff: can("users.manage"), company: can("settings.manage"),
    woo: canAny(["stock.view", "products.view"]) && moduleOn("stock"), epos: can("epos.manage") && moduleOn("epos"),
    shipping: canAny(["settings.view", "settings.manage", "orders.view", "operations.view"]),
    databases: can("databases.view"), imports: can("imports.manage"),
    integrations: canAny(["settings.manage", "email_marketing.settings"]), activity: can("audit.view") || can("audit.view_finance"),
  };
  const showDataGroup = (canAny(["stock.view", "products.view"]) && moduleOn("stock")) || can("audit.view") || can("audit.view_finance") || can("imports.manage") || can("databases.view") || can("forms.view") || can("communications.providers") || can("users.manage") || can("whatsapp.view") || can("whatsapp.settings.manage") || canAny(["email_marketing.view", "email_marketing.manage"]) || (can("epos.manage") && moduleOn("epos")) || isSuper;

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-[#f6f7fb]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky lg:top-0 z-40 h-screen w-64 shrink-0 bg-[#FBFBFD] border-r border-black/[0.06] flex flex-col transition-transform duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-100">
          <BrandLogo size={26} />
          <span className="text-lg font-bold tracking-tight text-slate-900">HTSS Ops</span>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Operations
          </p>
          <NavLink to="/" end className={navClass} data-testid="sidebar-nav-dashboard" onClick={() => setMobileOpen(false)}>
            <LayoutDashboard className="w-[18px] h-[18px]" strokeWidth={2} /> Dashboard
          </NavLink>
          {ted && (
          <NavLink to="/ted" className={navClass} data-testid="sidebar-nav-ted-command-centre" onClick={() => setMobileOpen(false)}>
            <Sparkles className="w-[18px] h-[18px]" strokeWidth={2} /> Ted Command Centre
          </NavLink>
          )}
          {intelOn && !ted?.unified && (
          <NavLink to="/ted-insights" className={navClass} data-testid="sidebar-nav-ted-insights" onClick={() => setMobileOpen(false)}>
            <Lightbulb className="w-[18px] h-[18px]" strokeWidth={2} /> Ted Insights
          </NavLink>
          )}
          {salesIntel && (
          <NavLink to="/sales-intelligence" className={navClass} data-testid="sidebar-nav-sales-intelligence" onClick={() => setMobileOpen(false)}>
            <Sparkles className="w-[18px] h-[18px]" strokeWidth={2} /> Sales Intelligence
          </NavLink>
          )}
          <NavLink to="/marketplace" className={navClass} data-testid="sidebar-nav-marketplace" onClick={() => setMobileOpen(false)}>
            <Store className="w-[18px] h-[18px]" strokeWidth={2} /> Marketplace
            <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5">Coming soon</span>
          </NavLink>

          {canAny(["orders.view", "operations.view", "projects.view"]) && (moduleOn("tasks") || moduleOn("orders")) && (
            <NavGroup icon={ClipboardList} label="Jobs" open={openGroup === "jobs"} onToggle={() => toggleGroup("jobs")} testId="sidebar-nav-jobs">
              {canAny(["orders.view", "operations.view"]) && <SubLink to="/tasks" label="All Jobs" testId="sidebar-nav-tasks" setMobileOpen={setMobileOpen} alert={newEnq} />}
              {can("orders.view") && !hide("web_sales") && <SubLink to="/web-sales" label="Web Sales" testId="sidebar-nav-web-sales" setMobileOpen={setMobileOpen} />}
              {can("orders.view") && !hide("shop_sales") && <SubLink to="/shop" label="Shop Sales" testId="sidebar-nav-shop" setMobileOpen={setMobileOpen} count={shopOpen > 0 ? shopOpen : undefined} />}
            </NavGroup>
          )}

          {!hide("operations") && canAny(["operations.view", "deliveries.view", "projects.view"]) && (moduleOn("scheduler") || moduleOn("projects") || moduleOn("fleet") || moduleOn("forms")) && (
            <NavGroup icon={Truck} label="Operations" open={openGroup === "operations"} onToggle={() => toggleGroup("operations")} testId="sidebar-nav-operations">
              {can("work_bank.view") && <SubLink to="/job-pool" label="Job Pool" testId="sidebar-nav-work-bank" setMobileOpen={setMobileOpen} />}
              {can("operations.view") && <SubLink to="/scheduler" label="Scheduler" testId="sidebar-nav-scheduler" setMobileOpen={setMobileOpen} />}
              {can("operations.view") && <SubLink to="/operations/rebook" label="Rebook Jobs" testId="sidebar-nav-rebook-dashboard" setMobileOpen={setMobileOpen} />}
              {can("operations.view") && <SubLink to="/operations/forklift-checks" label="Forklift Check Sheets" testId="sidebar-nav-forklift-checks" setMobileOpen={setMobileOpen} />}
              {canAny(["deliveries.view", "operations.view"]) && <SubLink to="/live-tracking" label="Live Tracking" testId="sidebar-nav-live-tracking" setMobileOpen={setMobileOpen} />}
              {canAny(["mobile.warehouse.movements", "deliveries.view", "operations.view"]) && <SubLink to="/load-board" label="Load Board" testId="sidebar-nav-load-board" setMobileOpen={setMobileOpen} />}
              {can("operations.view") && <SubLink to="/runs" label="Runs" testId="sidebar-nav-runs" setMobileOpen={setMobileOpen} />}
              {can("projects.view") && <SubLink to="/projects" label="Projects" testId="sidebar-nav-projects" setMobileOpen={setMobileOpen} />}
              {can("operations.view") && <SubLink to="/reports/hotspots" label="Disruption Hotspots" testId="sidebar-nav-hotspots" setMobileOpen={setMobileOpen} />}
              {can("forms.view") && <SubLink to="/forms" label="Forms" testId="sidebar-nav-forms" setMobileOpen={setMobileOpen} />}
              {canAny(["communications.view", "communications.manage"]) && <SubLink to="/communications/inbox" label="Unassigned Inbox" testId="sidebar-nav-comms-inbox" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}

          {can("customers.view") && (
            <NavGroup icon={UsersIcon} label="Users" open={openGroup === "users"} onToggle={() => toggleGroup("users")} testId="sidebar-nav-users-group">
              <SubLink to="/users" label="Customers" testId="sidebar-nav-users" setMobileOpen={setMobileOpen} />
            </NavGroup>
          )}

          {canAny(["products.view", "stock.view", "orders.view"]) && (moduleOn("stock") || moduleOn("orders")) && (
            <NavGroup icon={Package} label="Products" open={openGroup === "products"} onToggle={() => toggleGroup("products")} testId="sidebar-nav-products-group">
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products" label="Product list" testId="sidebar-nav-products" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products?group=Accessories" label="Accessories" testId="sidebar-nav-products-accessories" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products/bundles" label="Bundles" testId="sidebar-nav-products-bundles" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products/subscriptions" label="Subscriptions" testId="sidebar-nav-products-subscriptions" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products?group=Parts" label="Parts" testId="sidebar-nav-products-parts" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products?group=Tubs" label="Hot Tubs" testId="sidebar-nav-products-tubs" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products?group=Saunas" label="Saunas" testId="sidebar-nav-products-saunas" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products?group=Swim%20Spas" label="Swim Spas" testId="sidebar-nav-products-swim-spas" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products?group=Cold%20Plunges" label="Cold Plunges" testId="sidebar-nav-products-cold-plunges" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products?group=Pergolas" label="Pergolas" testId="sidebar-nav-products-pergolas" setMobileOpen={setMobileOpen} />}
              {canAny(["products.view", "stock.view"]) && <SubLink to="/products?group=Covanas" label="Covanas" testId="sidebar-nav-products-covanas" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}

          {!hide("stock") && canAny(["stock.view", "products.view"]) && moduleOn("stock") && (<>
          <button
            type="button"
            onClick={() => toggleGroup("stock")}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-150"
            data-testid="sidebar-nav-stock"
          >
            <Boxes className="w-[18px] h-[18px]" strokeWidth={2} />
            <span className="flex-1 text-left">Stock Control</span>
            {openGroup === "stock" ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          </button>
          {openGroup === "stock" && (
            <div className="ml-4 pl-3 border-l border-slate-100 space-y-0.5">
              {[
                { to: "/stock", label: "Overview", icon: Boxes, testId: "sidebar-nav-stock-overview" },
                { to: "/stock/spa-units", label: "Hot Tub Units", icon: Boxes, testId: "sidebar-nav-spa-units" },
                { to: "/orders", label: "Orders", icon: Package, testId: "sidebar-nav-orders", perm: "orders.view" },
                { to: "/stock/locations", label: "By Location", icon: MapPin, testId: "sidebar-nav-stock-locations" },
                { to: "/stock/movements", label: "Movements", icon: History, testId: "sidebar-nav-stock-movements" },
                { to: "/stock/scan", label: "Scan", icon: ScanBarcode, testId: "sidebar-nav-stock-scan" },
                { to: "/stock/product-requests", label: "Product Requests", icon: ClipboardList, testId: "sidebar-nav-product-requests" },
                { to: "/stock/short-picks", label: "Short Picks", icon: AlertTriangle, testId: "sidebar-nav-short-picks" },
                { to: "/stock/requests", label: "Stock Requests", icon: ClipboardList, testId: "sidebar-nav-stock-requests", badge: srOpen },
              ].filter((s) => !s.perm || can(s.perm)).map((s) => (
                <NavLink
                  key={s.to}
                  to={s.to}
                  end={s.to === "/stock"}
                  className={({ isActive }) => [
                    "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[14px] font-medium transition-colors duration-150",
                    isActive ? "bg-indigo-600/[0.08] text-indigo-700" : "text-slate-700 hover:bg-slate-200/60 hover:text-slate-900",
                  ].join(" ")}
                  data-testid={s.testId}
                  onClick={() => setMobileOpen(false)}
                >
                  <s.icon className="w-4 h-4" strokeWidth={2} />
                  <span>{s.label}</span>
                  {s.badge > 0 && <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid={`${s.testId}-badge`}>{s.badge}</span>}
                </NavLink>
              ))}
            </div>
          )}
          </>)}

          {canAny(["epos.view", "epos.sell", "epos.manage", "epos.reports"]) && moduleOn("epos") && (
            <NavGroup icon={MonitorSmartphone} label="EPOS" open={openGroup === "epos"} onToggle={() => toggleGroup("epos")} testId="sidebar-nav-epos-group">
              <SubLink to="/epos" end label="Registers" testId="sidebar-nav-epos" setMobileOpen={setMobileOpen} />
              {!hide("shop_sales") && <SubLink to="/epos/sales" label="Shop Sales" testId="sidebar-nav-epos-sales" setMobileOpen={setMobileOpen} />}
              {can("epos.reports") && <SubLink to="/epos/reports" label="Reports" testId="sidebar-nav-epos-reports" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}

          {(can("finance.profit_loss.view") || can("quotes.view") || can("expenses.view")) && (moduleOn("finance") || moduleOn("quotes")) && (
            <NavGroup icon={Landmark} label="Finance" open={openGroup === "finance"} onToggle={() => toggleGroup("finance")} testId="sidebar-nav-finance-group">
              {can("finance.profit_loss.view") && <SubLink to="/finance/reports?report=pnl" label="Management P&L" testId="sidebar-nav-finance" setMobileOpen={setMobileOpen} />}
              {can("expenses.view") && <SubLink to="/finance/expenses" label="Expenses" testId="sidebar-nav-expenses" setMobileOpen={setMobileOpen} />}
              {canAny(["finance.view", "expenses.view"]) && <SubLink to="/finance/bills" label="Bills" testId="sidebar-nav-bills" setMobileOpen={setMobileOpen} />}
              {canAny(["finance.view", "expenses.view", "expenses.approve", "finance.profit_loss.view", "finance.manage"]) && <SubLink to="/finance/reports" label="Reports" testId="sidebar-nav-finance-reports" setMobileOpen={setMobileOpen} />}
              {can("quotes.view") && <SubLink to="/quotes" label="Quotes" testId="sidebar-nav-quotes" setMobileOpen={setMobileOpen} />}
              {can("quotes.view") && <SubLink to="/invoices" label="Invoices" testId="sidebar-nav-invoices" setMobileOpen={setMobileOpen} />}
              {(can("finance.view") || can("quotes.view")) && <SubLink to="/finance/overdue-invoices" label="Overdue invoices" testId="sidebar-nav-overdue-invoices" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}

          {canAny(["absences.view", "holidays.request", "timesheets.view", "hr.documents.view", "hr.documents.manage"]) && moduleOn("hr") && (
            <NavGroup icon={CalendarDays} label="HR" open={openGroup === "hr"} onToggle={() => toggleGroup("hr")} testId="sidebar-nav-hr">
              {can("absences.view") && <SubLink to="/absences" label="Absences" testId="sidebar-nav-absences" setMobileOpen={setMobileOpen} />}
              {can("holidays.request") && <SubLink to="/holidays" label="Holidays" testId="sidebar-nav-holidays" setMobileOpen={setMobileOpen} />}
              {can("timesheets.view") && <SubLink to="/timesheets" label="Timesheets" testId="sidebar-nav-timesheets" setMobileOpen={setMobileOpen} />}
              <SubLink to="/hr/documents" label="HR Documents" testId="sidebar-nav-hr-documents" setMobileOpen={setMobileOpen} />
            </NavGroup>
          )}

          {!hide("marketing") && canAny(["orders.view", "forms.manage", "customers.view", "whatsapp.view", "whatsapp.settings.manage", "email_marketing.view", "email_marketing.manage"]) && (
            <NavGroup icon={Megaphone} label="Marketing & Media" open={openGroup === "marketing"} onToggle={() => toggleGroup("marketing")} testId="sidebar-nav-marketing">
              {canAny(["orders.view", "forms.manage"]) && <SubLink to="/media-library" label="Installation Media" testId="sidebar-nav-media-library" setMobileOpen={setMobileOpen} />}
              {can("customers.view") && <SubLink to="/reviews" label="Trustpilot Reviews" testId="sidebar-nav-reviews" setMobileOpen={setMobileOpen} />}
              {(can("whatsapp.view") || can("whatsapp.settings.manage")) && <SubLink to="/communications/whatsapp" label="WhatsApp" testId="sidebar-nav-whatsapp" setMobileOpen={setMobileOpen} />}
              {canAny(["email_marketing.view", "email_marketing.manage"]) && <SubLink to="/email-marketing" label="Email Marketing" testId="sidebar-nav-email-marketing" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}

          {!hide("analytics") && can("customers.view") && (
            <NavGroup icon={TrendingUp} label="Analytics" open={openGroup === "analytics"} onToggle={() => toggleGroup("analytics")} testId="sidebar-nav-analytics">
              <SubLink to="/marketing/website-analytics" label="Google Analytics" testId="sidebar-nav-website-analytics" setMobileOpen={setMobileOpen} />
              {can("orders.view") && <SubLink to="/analytics/enquiry-outcomes" label="Enquiry Outcomes" testId="sidebar-nav-enquiry-outcomes" setMobileOpen={setMobileOpen} />}
              <SubLink to="/analytics/google-ads" label="Google Ads" testId="sidebar-nav-google-ads" setMobileOpen={setMobileOpen} />
              <SubLink to="/analytics/search-console" label="Search Console" testId="sidebar-nav-search-console" setMobileOpen={setMobileOpen} />
              <SubLink to="/marketing/trends" label="Google Trends" testId="sidebar-nav-google-trends" setMobileOpen={setMobileOpen} />
            </NavGroup>
          )}

          <div className="pt-4"><FeedbackNavButton onClick={() => setMobileOpen(false)} /></div>

          {!hide("data") && (<>
          {showDataGroup && (
          <p className="px-3 pt-5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Data
          </p>
          )}
          {(dc.comms || dc.whatsapp || dc.emailLaunch || dc.notif) && (
            <NavGroup icon={MessageSquareIcon} label="Communications" open={dataGroup === "comms"} onToggle={() => toggleData("comms")} testId="sidebar-nav-comms-group">
              {dc.comms && <SubLink to="/communications/settings" label="Communications" testId="sidebar-nav-comms-settings" setMobileOpen={setMobileOpen} />}
              {dc.whatsapp && <SubLink to="/data/whatsapp" label="WhatsApp Setup" testId="sidebar-nav-whatsapp-setup" setMobileOpen={setMobileOpen} />}
              {dc.emailLaunch && <SubLink to="/data/email-launch" label="Email Launch Readiness" testId="sidebar-nav-email-launch" setMobileOpen={setMobileOpen} />}
              {dc.notif && <SubLink to="/admin/notifications" label="Notifications & Alerts" testId="sidebar-nav-notifications-admin" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}
          {(dc.staff || dc.company) && (
            <NavGroup icon={UserCog} label="People & Company" open={dataGroup === "people"} onToggle={() => toggleData("people")} testId="sidebar-nav-people-group">
              {dc.staff && <SubLink to="/admin/users-roles" label="Staff & Roles" testId="sidebar-nav-users-roles" setMobileOpen={setMobileOpen} />}
              {dc.company && <SubLink to="/settings/company" label="Company" testId="sidebar-nav-company" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}
          {(dc.woo || dc.epos || dc.shipping) && (
            <NavGroup icon={Boxes} label="Commerce & Shipping" open={dataGroup === "commerce"} onToggle={() => toggleData("commerce")} testId="sidebar-nav-commerce-group">
              {dc.woo && <SubLink to="/stock/woocommerce" label="WooCommerce" testId="sidebar-nav-wc-stock" setMobileOpen={setMobileOpen} />}
              {dc.epos && <SubLink to="/epos/settings" label="EPOS Settings" testId="sidebar-nav-epos-settings" setMobileOpen={setMobileOpen} />}
              {dc.shipping && <SubLink to="/settings/shipping" label="Shipping" testId="sidebar-nav-shipping" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}
          {(dc.imports || isSuper) && (
            <NavGroup icon={DatabaseZap} label="Data Management" open={dataGroup === "datamgmt"} onToggle={() => toggleData("datamgmt")} testId="sidebar-nav-datamgmt-group">
              {dc.imports && <SubLink to="/data/woocommerce-history" label="Order History Import" testId="sidebar-nav-wc-history" setMobileOpen={setMobileOpen} />}
              {dc.imports && <SubLink to="/imports" label="Import Validation" testId="sidebar-nav-import-validation" setMobileOpen={setMobileOpen} />}
              {isSuper && <SubLink to="/admin/quarantine" label="Quarantine" testId="sidebar-nav-quarantine" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}
          {(dc.integrations || dc.activity) && (
            <NavGroup icon={Plug} label="System & Integrations" open={dataGroup === "system"} onToggle={() => toggleData("system")} testId="sidebar-nav-system-group">
              {dc.integrations && <SubLink to="/settings/integrations" label="Integrations" testId="sidebar-nav-integrations" setMobileOpen={setMobileOpen} />}
              {dc.activity && <SubLink to="/admin/activity-log" label="Activity Log" testId="sidebar-nav-activity-log" setMobileOpen={setMobileOpen} />}
            </NavGroup>
          )}
          {dc.databases && (<>
          <button
            type="button"
            onClick={() => setDbOpen((o) => !o)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-150"
            data-testid="sidebar-nav-databases"
          >
            <Database className="w-[18px] h-[18px]" strokeWidth={2} />
            <span className="flex-1 text-left">Databases</span>
            {dbOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          </button>
          {dbOpen && (
            <div className="ml-4 pl-3 border-l border-slate-100 space-y-0.5" data-testid="sidebar-nav-databases-list">
              {isPlatformAdmin && (
                <NavLink to="/admin/log-viewer" className={subLinkClass} data-testid="sidebar-nav-log-viewer" onClick={() => setMobileOpen(false)}>
                  <span>Log Viewer</span>
                </NavLink>
              )}
              <NavLink
                to="/crews"
                className={({ isActive }) => [
                  "flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg text-[14px] font-medium transition-colors duration-150",
                  isActive ? "bg-indigo-600/[0.08] text-indigo-700" : "text-slate-700 hover:bg-slate-200/60 hover:text-slate-900",
                ].join(" ")}
                data-testid="sidebar-nav-db-crews"
                onClick={() => setMobileOpen(false)}
              >
                <span>Crews</span>
                {crewCount != null && <span className="text-[10px] text-slate-400 tabular-nums">{crewCount}</span>}
              </NavLink>
              {canAny(["deliveries.view", "operations.view"]) && (
                <NavLink to="/plant-transport" className={subLinkClass} data-testid="sidebar-nav-plant-transport" onClick={() => setMobileOpen(false)}>
                  <span>Plant Transport</span>
                  {vehicleCount != null && <span className="text-[10px] text-slate-400 tabular-nums">{vehicleCount}</span>}
                </NavLink>
              )}
              {dbTables.map((t) => (
                <NavLink
                  key={t.key}
                  to={`/databases/${t.key}`}
                  className={({ isActive }) => [
                    "flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg text-[14px] font-medium transition-colors duration-150",
                    isActive ? "bg-indigo-600/[0.08] text-indigo-700" : "text-slate-700 hover:bg-slate-200/60 hover:text-slate-900",
                  ].join(" ")}
                  data-testid={`sidebar-nav-db-${t.key}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span>{t.label}</span>
                  <span className="text-[10px] text-slate-400 tabular-nums">{t.count}</span>
                </NavLink>
              ))}
            </div>
          )}
          </>)}
          </>)}
        </nav>

        <div className="px-4 py-3 border-t border-slate-100 text-[11px] text-slate-400">
          HTSS Operations · POC
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/20 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 lg:px-6 sticky top-0 z-20">
          <button
            className="lg:hidden p-2 -ml-1 rounded-md hover:bg-slate-100"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <ChevronDown className="w-5 h-5 rotate-[-90deg]" />
          </button>

          <div className="relative hidden sm:flex items-center flex-1 max-w-sm">
            <Search className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none z-10" />
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="topbar-search-open"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-slate-50 border border-slate-200 text-slate-400 hover:bg-slate-100 hover:border-slate-300 transition-colors text-left"
            >
              Search jobs, customers, parts, shifts…
            </button>
          </div>

          <div className="flex-1" />

          {ted && (
            <button type="button" onClick={() => setTedOpen(true)} data-testid="topbar-ted-open"
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white px-2.5 py-1.5 text-xs font-semibold hover:bg-indigo-700 transition-colors">
              <Sparkles className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Ask Ted</span>
            </button>
          )}
          {ted && <TedDrawer open={tedOpen} onOpenChange={setTedOpen} status={ted} />}
          {expOpen && createPortal(<ExpenseForm onClose={() => setExpOpen(false)} onSaved={() => {}} />, document.body)}

          {/* Workspace (company) switcher */}
          {companies.length > 0 && (
            <div className="hidden sm:flex items-center" data-testid="workspace-switcher">
              {companies.length === 1 ? (
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700" title="Active workspace" data-testid="workspace-active">
                  <Building2 className="w-3.5 h-3.5 text-indigo-500" /> {companies[0].company_name}
                </span>
              ) : (
                <select
                  value={localStorage.getItem("operix_company_id") || user?.active_company_id || ""}
                  onChange={(e) => switchCompany(e.target.value)}
                  className="rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  data-testid="workspace-select"
                >
                  {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Demonstration label removed per request */}


          {/* Demo: View as role type */}
          {switchableRoles.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="view-as-role-dropdown"
                  className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                    previewing
                      ? "bg-violet-600 text-white border-violet-600 hover:bg-violet-700"
                      : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                  }`}
                  title="Demo: preview the app as a role type"
                >
                  <UserCog className="w-3.5 h-3.5" />
                  <span className="hidden md:block">
                    {previewing ? `Viewing as: ${effectiveRoleName}` : "View as"}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold text-slate-900">Preview as role type</div>
                  <div className="text-[11px] text-slate-500 font-normal">
                    Demo only — changes what this role can see. Your login is unchanged.
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-2"
                  data-testid="view-as-option-actual"
                  onClick={() => setPreviewRole(null)}
                >
                  <span className="w-4 flex justify-center">
                    {!previewing && <Check className="w-4 h-4 text-violet-600" />}
                  </span>
                  <span className="flex-1">My actual role</span>
                  <span className="text-[10px] text-slate-400 capitalize">{user?.role}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {switchableRoles.map((r) => (
                  <DropdownMenuItem
                    key={r.key}
                    className="gap-2"
                    data-testid={`view-as-option-${r.key}`}
                    onClick={() => setPreviewRole(r.key)}
                  >
                    <span className="w-4 flex justify-center">
                      {previewRoleKey === r.key && <Check className="w-4 h-4 text-violet-600" />}
                    </span>
                    <span className="flex-1 truncate">{r.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Notifications */}
          <NotificationsPanel notifs={notifs} anns={anns} returnsCount={returnsCount} onChanged={loadNotifs} />

          {/* Dark / light theme toggle */}
          <button
            onClick={() => setDark((d) => !d)}
            data-testid="theme-toggle"
            title={dark ? "Switch to light mode" : "Switch to dark mode"}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
          >
            {dark ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
          </button>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-profile-dropdown"
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <span className="w-8 h-8 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
                  {initials}
                </span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
                <div className="text-xs text-slate-500 font-normal truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuSeparator />
              {isPlatformAdmin && (
                <>
                  <DropdownMenuItem asChild className="gap-2 cursor-pointer" data-testid="nav-platform-admin">
                    <NavLink to="/platform"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Platform Admin</NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem asChild className="gap-2 cursor-pointer" data-testid="nav-my-profile">
                <NavLink to="/profile"><UserCog className="w-4 h-4 text-indigo-500" /> My Profile</NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="gap-2 cursor-pointer" data-testid="nav-my-notes">
                <NavLink to="/me/notes"><StickyNote className="w-4 h-4 text-amber-500" /> My Notes</NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="gap-2 cursor-pointer" data-testid="nav-my-tasks">
                <NavLink to="/me/tasks"><ListTodo className="w-4 h-4 text-indigo-500" /> My Tasks</NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 cursor-pointer" onSelect={() => setExpOpen(true)} data-testid="nav-add-expense">
                <Receipt className="w-4 h-4 text-emerald-500" /> Add Expense
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 cursor-default">
                <ShieldCheck className="w-4 h-4 text-indigo-500" />
                <span className="capitalize">{effectiveRole} role</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 text-rose-600 focus:text-rose-700"
                onClick={logout}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

        {supportSession && (
          <div
            data-testid="support-session-banner"
            className="flex items-center gap-3 bg-violet-700 text-white px-4 lg:px-6 py-2 text-sm"
          >
            <LifeBuoy className="w-4 h-4 shrink-0" />
            <span className="flex-1">
              Support Session — acting as <b className="capitalize">{supportSession.acting_role?.replace("_", " ")}</b> at{" "}
              <b>{supportSession.company_name}</b>
              <span className="ml-2 rounded bg-white/20 px-1.5 py-0.5 text-[11px] font-semibold uppercase">{supportSession.mode}</span>
            </span>
            <button
              type="button"
              data-testid="end-support-session-button"
              onClick={endSupportSession}
              className="rounded-md bg-white text-violet-700 px-2.5 py-1 text-[12px] font-bold hover:bg-violet-50 transition-colors"
            >
              End session
            </button>
          </div>
        )}

        {previewing && (
          <div
            data-testid="preview-banner"
            className="flex items-center gap-3 bg-violet-600 text-white px-4 lg:px-6 py-2 text-sm"
          >
            <UserCog className="w-4 h-4 shrink-0" />
            <span className="flex-1">
              Demo preview — you are viewing the app as <b>{effectiveRoleName}</b>. Navigation and
              access reflect this role.
            </span>
            {effectiveRole === "customer" && (
              <a
                href="/portal/demo"
                target="_blank"
                rel="noreferrer"
                data-testid="preview-open-portal"
                className="inline-flex items-center gap-1.5 rounded-md bg-white/15 hover:bg-white/25 px-2.5 py-1 text-[12px] font-semibold transition-colors"
              >
                Open customer portal <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <button
              type="button"
              data-testid="exit-preview-button"
              onClick={() => setPreviewRole(null)}
              className="rounded-md bg-white text-violet-700 px-2.5 py-1 text-[12px] font-bold hover:bg-violet-50 transition-colors"
            >
              Exit preview
            </button>
          </div>
        )}

        <main className="flex-1 overflow-y-auto">
          {blockedRoute ? <Navigate to="/" replace /> : <Outlet />}
        </main>
        <FeedbackWidget />
      </div>
    </div>
  );
}
